// IngredientManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, TextField, Button, Box, Stack, MenuItem, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Switch, FormControlLabel, IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  collection, getDocs, setDoc, updateDoc, deleteDoc, doc
} from 'firebase/firestore';
import { db } from './firebase';

const INGREDIENT_COLLECTION = 'recipe_ingredients';
const VENDOR_COLLECTION = 'vendors';
const UNIT_COLLECTION = 'ingredient_units';

// Allowed categories for the unified system
const ALLOWED_TYPES = ['Beverage', 'Cooking', 'Mixing', 'Prep', 'Dessert'];

// Ensure MUI <Select multiple> always receives an array
const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const emptyForm = {
  id: '',
  name: '',
  // Multi-select Types; default to Beverage for new adds
  types: ['Beverage'],
  sku: '',
  vendor_id: '',
  unit_calibrations: [] // [{unit_id, grams_per_unit?, ml_per_unit?}]
};

const IngredientManager = () => {
  const [ingredients, setIngredients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [units, setUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [confirmRestore, setConfirmRestore] = useState({ open: false, archivedItem: null });
  const [confirmAdd, setConfirmAdd] = useState({ open: false, payload: null });
  const [confirmUpdate, setConfirmUpdate] = useState({ open: false, payload: null });
  const [confirmArchive, setConfirmArchive] = useState({ open: false, id: null });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    const [ingSnap, vendSnap, unitSnap] = await Promise.all([
      getDocs(collection(db, INGREDIENT_COLLECTION)),
      getDocs(collection(db, VENDOR_COLLECTION)),
      getDocs(collection(db, UNIT_COLLECTION))
    ]);

    // Back-compat normalize: if a doc only has legacy "type", fold into types[0]
    const normalized = ingSnap.docs.map(d => {
      const data = d.data() || {};
      let types = asArray(data.types);
      if (types.length === 0 && data.type) types = [data.type];
      return { id: d.id, ...data, types };
    });

    setIngredients(normalized);
    setVendors(vendSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setUnits(unitSnap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleTypesChange = (e) => {
    setForm(prev => ({ ...prev, types: asArray(e.target.value) }));
  };

  const addCalibration = () => {
    setForm(prev => ({
      ...prev,
      unit_calibrations: [...(prev.unit_calibrations || []), { unit_id: '', grams_per_unit: '', ml_per_unit: '' }]
    }));
  };

  const removeCalibration = (idx) => {
    setForm(prev => ({
      ...prev,
      unit_calibrations: prev.unit_calibrations.filter((_, i) => i !== idx)
    }));
  };

  const updateCalibration = (idx, key, value) => {
    setForm(prev => {
      const next = [...(prev.unit_calibrations || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, unit_calibrations: next };
    });
  };

  const isFiniteNum = (v) => typeof v === 'number' && Number.isFinite(v);

  const pruneUndefinedDeep = (val) => {
    if (Array.isArray(val)) return val.map(pruneUndefinedDeep);
    if (val && typeof val === 'object') {
      const out = {};
      for (const [k, v2] of Object.entries(val)) {
        if (v2 === undefined) continue;
        const pv = pruneUndefinedDeep(v2);
        if (pv !== undefined) out[k] = pv;
      }
      return out;
    }
    return val === undefined ? undefined : val;
  };

  const normalizePayload = (payload) => {
    // sanitize types -> allowed + dedup (and ensure non-empty)
    const cleanTypes = Array.from(
      new Set(asArray(payload.types).map(t => String(t || '').trim()).filter(t => ALLOWED_TYPES.includes(t)))
    );
    const finalTypes = cleanTypes.length ? cleanTypes : ['Beverage'];

    // dedupe calibrations by unit_id and require at least one numeric
    const seen = new Set();
    const cal = (payload.unit_calibrations || [])
      .map((c) => {
        const unit_id = (c.unit_id || '').trim();
        const g = c.grams_per_unit === '' ? undefined : Number(c.grams_per_unit);
        const ml = c.ml_per_unit === '' ? undefined : Number(c.ml_per_unit);
        const out = { unit_id };
        if (isFiniteNum(g)) out.grams_per_unit = g;
        if (isFiniteNum(ml)) out.ml_per_unit = ml;
        return out;
      })
      .filter((c) => {
        if (!c.unit_id) return false;
        const keep = isFiniteNum(c.grams_per_unit) || isFiniteNum(c.ml_per_unit);
        if (!keep || seen.has(c.unit_id)) return false;
        seen.add(c.unit_id);
        return true;
      });

    const result = {
      id: payload.id,
      name: payload.name,
      types: finalTypes,
      // keep legacy "type" for any old readers (first element)
      type: finalTypes[0] || 'Beverage',
      sku: payload.sku || '',
      vendor_id: payload.vendor_id || '',
      archived: !!payload.archived,
      unit_calibrations: cal
    };

    return pruneUndefinedDeep(result);
  };

  const handleSubmit = () => {
    if (!form.id || !form.name) return;
    const payload = normalizePayload({ ...form });

    const activeExists = ingredients.some(i => i.id === form.id && !i.archived);
    const archivedItem = ingredients.find(i => i.id === form.id && i.archived);

    if (!editing && activeExists) {
      setSnackbar({ open: true, message: 'Ingredient ID already exists!', severity: 'warning' });
      return;
    }
    if (!editing && archivedItem) {
      setConfirmRestore({ open: true, archivedItem });
      return;
    }
    if (editing) {
      setConfirmUpdate({ open: true, payload });
    } else {
      setConfirmAdd({ open: true, payload });
    }
  };

  const applyAdd = async () => {
    const ref = doc(db, INGREDIENT_COLLECTION, confirmAdd.payload.id);
    const clean = pruneUndefinedDeep(confirmAdd.payload);
    await setDoc(ref, clean);
    await fetchAll();
    resetForm();
    setConfirmAdd({ open: false, payload: null });
    setSnackbar({ open: true, message: 'Ingredient added.', severity: 'success' });
  };

  const applyUpdate = async () => {
    const ref = doc(db, INGREDIENT_COLLECTION, confirmUpdate.payload.id);
    await updateDoc(ref, confirmUpdate.payload);
    await fetchAll();
    resetForm();
    setConfirmUpdate({ open: false, payload: null });
    setSnackbar({ open: true, message: 'Ingredient updated.', severity: 'success' });
  };

  const restoreArchivedItem = async (item = null) => {
    const archived = item || confirmRestore.archivedItem;
    if (!archived) return;
    const ref = doc(db, INGREDIENT_COLLECTION, archived.id);
    await updateDoc(ref, { archived: false });
    await fetchAll();
    resetForm();
    setConfirmRestore({ open: false, archivedItem: null });
    setSnackbar({ open: true, message: 'Ingredient restored.', severity: 'info' });
  };

  const archiveIngredient = async () => {
    const id = confirmArchive.id;
    await updateDoc(doc(db, INGREDIENT_COLLECTION, id), { archived: true });
    await fetchAll();
    setConfirmArchive({ open: false, id: null });
    setSnackbar({ open: true, message: 'Ingredient archived.', severity: 'info' });
  };

  const deleteIngredient = async () => {
    const id = confirmDelete.id;
    setLoading(true);
    try {
      await deleteDoc(doc(db, INGREDIENT_COLLECTION, id));
      await fetchAll();
      setSnackbar({ open: true, message: 'Ingredient permanently deleted.', severity: 'success' });
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'Error deleting ingredient.', severity: 'error' });
    } finally {
      setConfirmDelete({ open: false, id: null });
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setFormOpen(true);
    setEditing(item);
    // fold legacy item.type into array if item.types is missing/empty
    let types = asArray(item.types);
    if (types.length === 0 && item.type) types = [item.type];
    if (types.length === 0) types = ['Beverage'];
    setForm({
      id: item.id,
      name: item.name,
      types,
      sku: item.sku || '',
      vendor_id: item.vendor_id || '',
      unit_calibrations: item.unit_calibrations && item.unit_calibrations.length
        ? item.unit_calibrations
        : []
    });
  };

  const resetForm = () => { setForm(emptyForm); setEditing(null); setFormOpen(false); };

  const filtered = ingredients.filter(i =>
    (showArchived ? i.archived : !i.archived) &&
    (i.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isIdTaken = !editing && ingredients.some(i => i.id === form.id && !i.archived);
  const isIdInvalid = !form.id || isIdTaken;
  const getVendor = (id) => vendors.find(v => v.id === id)?.name || id;
  const unitLabel = (id) => units.find(u => u.id === id)?.label || id;

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h4" align="center">🥣 Ingredient Manager</Typography>

      {/* CONFIRM DIALOGS */}
      <Dialog open={confirmAdd.open} onClose={() => setConfirmAdd({ open: false, payload: null })}>
        <DialogTitle>Confirm Add</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmAdd({ open: false, payload: null })}>Cancel</Button>
          <Button onClick={applyAdd}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmUpdate.open} onClose={() => setConfirmUpdate({ open: false, payload: null })}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmUpdate({ open: false, payload: null })}>Cancel</Button>
          <Button onClick={applyUpdate}>Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRestore.open} onClose={() => setConfirmRestore({ open: false, archivedItem: null })}>
        <DialogTitle>Restore Archived Ingredient?</DialogTitle>
        <DialogContent>
          This ID exists in archived data: <strong>{confirmRestore.archivedItem?.name}</strong>.<br />
          Restore it instead of creating new?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestore({ open: false, archivedItem: null })}>Cancel</Button>
          <Button onClick={() => restoreArchivedItem()}>Restore</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmArchive.open} onClose={() => setConfirmArchive({ open: false, id: null })}>
        <DialogTitle>Confirm Archive</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmArchive({ open: false, id: null })}>Cancel</Button>
          <Button color="warning" onClick={archiveIngredient}>Archive</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, id: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, id: null })}>Cancel</Button>
          <Button color="error" onClick={deleteIngredient} disabled={loading}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* FORM */}
      <Box sx={{ my: 3 }}>
        <Button variant="outlined" fullWidth onClick={() => setFormOpen(!formOpen)}>
          {formOpen ? <><ExpandLessIcon /> Hide Form</> : <><ExpandMoreIcon /> Add New Ingredient</>}
        </Button>
        <Collapse in={formOpen}>
          <Stack spacing={2} mt={2}>
            <TextField
              label="ID"
              name="id"
              value={form.id}
              onChange={handleChange}
              disabled={!!editing}
              error={isIdTaken}
              helperText={isIdTaken ? 'This ID is taken.' : ''}
            />
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} />

            {/* Multi-select Types */}
            <TextField
              select
              label="Types"
              name="types"
              value={asArray(form.types)}
              onChange={handleTypesChange}
              SelectProps={{ multiple: true, renderValue: (selected) => (selected || []).join(', ') }}
              helperText="Select one or more categories"
            >
              {ALLOWED_TYPES.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>

            <TextField label="SKU" name="sku" value={form.sku} onChange={handleChange} />
            <TextField select label="Vendor" name="vendor_id" value={form.vendor_id} onChange={handleChange}>
              {vendors.map(v => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
            </TextField>

            {/* Calibration Editor */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1">Unit Calibrations</Typography>
                <Button startIcon={<AddIcon />} onClick={addCalibration}>Add Calibration</Button>
              </Stack>
              <Stack spacing={1}>
                {(form.unit_calibrations || []).map((cal, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <TextField
                      select
                      label="Unit"
                      value={cal.unit_id || ''}
                      onChange={(e) => updateCalibration(idx, 'unit_id', e.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      {units.map(u => <MenuItem key={u.id} value={u.id}>{u.label || u.id}</MenuItem>)}
                    </TextField>
                    <TextField
                      label="grams_per_unit"
                      type="number"
                      inputProps={{ step: 'any' }}
                      value={cal.grams_per_unit ?? ''}
                      onChange={(e) => updateCalibration(idx, 'grams_per_unit', e.target.value)}
                    />
                    <TextField
                      label="ml_per_unit"
                      type="number"
                      inputProps={{ step: 'any' }}
                      value={cal.ml_per_unit ?? ''}
                      onChange={(e) => updateCalibration(idx, 'ml_per_unit', e.target.value)}
                    />
                    <IconButton onClick={() => removeCalibration(idx)}><DeleteIcon /></IconButton>
                  </Stack>
                ))}
              </Stack>
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Enter either <b>grams_per_unit</b> (mass) or <b>ml_per_unit</b> (volume) — or both when relevant.
              </Typography>
            </Paper>

            <Button variant="contained" onClick={handleSubmit} disabled={isIdInvalid}>{editing ? 'Update' : 'Submit'}</Button>
          </Stack>
        </Collapse>
      </Box>

      {/* LIST */}
      <TextField
        fullWidth
        label="Search Ingredients"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />
      <FormControlLabel control={<Switch checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />} label="Show Archived" />

      <Stack spacing={2}>
        {filtered.map(item => {
          let types = asArray(item.types);
          if (types.length === 0 && item.type) types = [item.type];
          return (
            <Paper key={item.id} sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                <Box>
                  <Typography fontWeight="bold">{item.name}</Typography>
                  <Typography variant="body2">ID: {item.id}</Typography>
                  <Typography variant="body2">Types: {types.length ? types.join(', ') : '—'}</Typography>
                  <Typography variant="body2">Vendor: {getVendor(item.vendor_id)}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Calibrations: {(item.unit_calibrations || []).length
                      ? (item.unit_calibrations || []).map(c => `${unitLabel(c.unit_id)} → ${c.grams_per_unit ? `${c.grams_per_unit} g` : ''}${c.grams_per_unit && c.ml_per_unit ? ' | ' : ''}${c.ml_per_unit ? `${c.ml_per_unit} ml` : ''}`).join(', ')
                      : '—'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  {!item.archived ? (
                    <>
                      <Button size="small" onClick={() => handleEdit(item)}>Edit</Button>
                      <Button size="small" color="warning" onClick={() => setConfirmArchive({ open: true, id: item.id })}>Archive</Button>
                    </>
                  ) : (
                    <>
                      <Button size="small" onClick={() => restoreArchivedItem(item)}>Restore</Button>
                      <Button size="small" color="error" onClick={() => setConfirmDelete({ open: true, id: item.id })} disabled={loading}>Delete</Button>
                    </>
                  )}
                </Stack>
              </Box>
            </Paper>
          );
        })}
      </Stack>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default IngredientManager;
